// crm.view — CRM/Loyalty: segmentasi + LTV + poin (earn/redeem) + broadcast WA.
import { rp, esc, ageDays, wa62, fillTpl, todayISO, maskPhone } from '@domain/format';
import { waTpl } from '@core/settings';
import { openModal } from '@ui/modal';
import { searchBoxHTML, wireSearchBox } from '@ui/searchbox';
import { toast } from '@ui/toast';
import { openWA } from '@core/wa';
import { currentUser } from '@core/session';
import { bisaBuka } from '@core/perms';
import * as repo from './crm.repo';
import * as loyaltyRepo from './loyalty.repo';
import * as afRepo from './afiliasi.repo';
import { renderAfiliasiTab } from './afiliasi.view';
import { renderUltahTab } from './ultah.view';
import { renderRfmTab } from './crm.rfm.view';
import { kodeTukarValid, bagiPemakaian } from '@domain/loyalty';
import * as svc from './crm.service';

interface Row extends repo.CrmCustomer { ltv: number; trx: number; last: string | null; earned: number; redeemed: number; avail: number; hangusNanti: number; tglHangus: string | null; sudahHangus: number; bonus: number; diajak: string | null }

const SEGS: [string, string][] = [['all', 'Semua'], ['User', 'User'], ['Toko', 'Toko'], ['sering', 'Sering (≥3)'], ['lama', 'Lama (>90h)']];
let seg = 'all';
let crQ = ''; // kotak cari (nama/WA/kota) — daftar pelanggan bisa ratusan baris

const inSeg = (r: Row): boolean => {
  switch (seg) {
    case 'User': return r.kat === 'User';
    case 'Toko': return r.kat === 'Toko';
    case 'sering': return r.trx >= 3;
    case 'lama': return r.trx >= 1 && ageDays(r.last) > 90;
    case 'ultah': return !!r.lahir && +r.lahir.slice(5, 7) === new Date().getMonth() + 1;
    default: return true;
  }
};

// Tiga tab (Apo 21 Jul): daftar pelanggan · afiliasi · ulang tahun. Ulang tahun & afiliasi
// dipisah karena keduanya pekerjaan tersendiri, bukan sekadar penyaring daftar pelanggan.
type CrmTab = 'pelanggan' | 'rfm' | 'afiliasi' | 'ultah';
let crTab: CrmTab = 'pelanggan';

export async function renderCRM(root: HTMLElement): Promise<void> {
  const TABS: [CrmTab, string][] = [['pelanggan', '👥 Pelanggan'], ['rfm', '📊 RFM'], ['afiliasi', '🤝 Afiliasi'], ['ultah', '🎂 Ulang Tahun']];
  const bar = `<div class="nt-tabs" style="border-radius:0;margin:10px 0 0">${TABS.map(([k, l]) =>
    `<button data-crtab="${k}" class="${crTab === k ? 'active' : ''}">${l}</button>`).join('')}</div>`;
  const pasangTab = (): void => {
    root.querySelectorAll<HTMLElement>('button[data-crtab]').forEach((b) => b.addEventListener('click', () => {
      crTab = b.dataset['crtab'] as CrmTab; void renderCRM(root);
    }));
  };
  if (crTab !== 'pelanggan') {
    root.innerHTML = `<div class="ttl">🤝 CRM / Loyalty</div>${bar}<div class="panel" id="cr_pane" style="padding:12px"></div>`;
    pasangTab();
    const pane = root.querySelector('#cr_pane') as HTMLElement;
    await (crTab === 'afiliasi' ? renderAfiliasiTab(pane) : crTab === 'rfm' ? renderRfmTab(pane) : renderUltahTab(pane));
    return;
  }
  await renderPelangganTab(root, bar, pasangTab);
}

async function renderPelangganTab(root: HTMLElement, bar: string, pasangTab: () => void): Promise<void> {
  root.innerHTML = `<div class="ttl">🤝 CRM / Loyalty</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let custs: repo.CrmCustomer[], spend: Map<number, repo.Spend>, redeemed: Map<number, number>;
  let grants = new Map<number, afRepo.Grant[]>(), refs: { id: number; nama: string; referrer_id: number | null }[] = [];
  try { [custs, spend, redeemed] = await Promise.all([repo.listCustomers(), repo.spendByCustomer(), repo.redeemedByCustomer()]); }
  catch (e) { root.innerHTML = `<div class="ttl">🤝 CRM / Loyalty</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  // Program loyalti tetap MILIK CABANG PENDAFTAR (keputusan lama F10). Sejak pelanggan bisa
  // terbaca lintas cabang (agar namanya muncul di antrian), batas itu harus ditegakkan di sini —
  // kalau tidak, poin & LTV pelanggan cabang lain ikut tampil dengan angka separuh (hanya tiket
  // cabang ini), yang menyesatkan petugas saat menukar poin.
  // Bonus afiliasi & tautan rekomendasi — opsional: kalau migrasinya belum di-push, CRM tetap jalan.
  try { [grants, refs] = await Promise.all([afRepo.grantsAktifSemua(), afRepo.daftarRekomendasi()]); } catch { /* afiliasi belum aktif */ }
  const namaById = new Map(custs.map((c) => [c.id, c.nama]));
  const diajakOleh = new Map(refs.map((r) => [r.id, r.referrer_id]));

  const u0 = currentUser();
  const milikCabang = (c: repo.CrmCustomer): boolean =>
    !u0 || ['Owner', 'Manager'].includes(u0.role) || !c.outlet || c.outlet === u0.outlet;

  const rows: Row[] = custs.filter(milikCabang).map((c) => {
    const s = spend.get(c.id) ?? { ltv: 0, trx: 0, last: null, per: [] };
    const red = redeemed.get(c.id) ?? 0;
    const g = (grants.get(c.id) ?? []).map((x) => ({ id: x.id, tgl: x.tgl, poin: x.poin, alasan: x.alasan, ditarik: !!x.ditarik_at, berlakuBulan: x.berlaku_bulan }));
    const sp = svc.saldo(s.per, red, undefined, g);
    return { ...c, ltv: s.ltv, trx: s.trx, last: s.last, earned: sp.totalDiperoleh, redeemed: red, avail: sp.tersedia,
      hangusNanti: sp.hangusNanti, tglHangus: sp.tglHangusTerdekat, sudahHangus: sp.totalHangus,
      bonus: g.filter((x) => !x.ditarik).reduce((a, x) => a + x.poin, 0),
      diajak: diajakOleh.get(c.id) ? (namaById.get(diajakOleh.get(c.id) as number) ?? null) : null };
  });
  const q = crQ.trim().toLowerCase();
  const shown = rows.filter(inSeg)
    .filter((r) => !q || `${r.nama} ${r.wa ?? ''} ${r.kota ?? ''}`.toLowerCase().includes(q))
    .sort((a, b) => b.ltv - a.ltv);
  const totalSeg = rows.filter(inSeg).length;
  const canR = svc.canCRM();

  const body = shown.map((r) => {
    const wa = wa62(r.wa ?? '');
    const badges = [r.trx >= 3 ? '🔁' : '', (r.trx >= 1 && ageDays(r.last) > 90) ? '💤' : '', (r.lahir && +r.lahir.slice(5, 7) === new Date().getMonth() + 1) ? '🎂' : ''].filter(Boolean).join(' ');
    const act = [
      wa ? `<button class="btn" style="padding:3px 8px" data-wa="${esc(wa)}" data-nama="${esc(r.nama)}" title="Kirim WhatsApp" aria-label="Kirim WhatsApp ke ${esc(r.nama)}">💬</button>` : '',
      (r.earned > 0 || r.redeemed > 0) ? `<button class="btn" style="padding:3px 8px" data-poinhist="${r.id}" data-nama="${esc(r.nama)}" data-e="${r.earned}" data-rd="${r.redeemed}" title="Riwayat poin" aria-label="Riwayat poin ${esc(r.nama)}">🧾</button>` : '',
      canR && r.avail > 0 && wa ? `<button class="btn" style="padding:3px 8px" data-link="${r.id}" data-wa="${esc(wa)}" data-nama="${esc(r.nama)}" title="Kirim tautan poin ke WhatsApp pelanggan">🔗 Tautan</button>` : '',
      canR && r.avail > 0 ? `<button class="btn" style="padding:3px 8px" data-redeem="${r.id}" data-avail="${r.avail}" data-nama="${esc(r.nama)}">🎁 Tukar</button>` : '',
    ].join(' ');
    return `<tr><td><b>${esc(r.nama)}</b> ${badges}${r.diajak ? `<br><span style="font-size:10.5px;color:var(--muted)">diajak ${esc(r.diajak)}</span>` : ''}</td><td>${esc(r.kat)}</td><td class="r">${rp(r.ltv)}</td><td class="r">${r.trx}</td><td>${r.last ? esc(r.last) : '—'}</td><td class="r"><b>${r.avail}</b>${r.bonus ? `<br><span style="font-size:10.5px;color:var(--ok)" title="Bonus rekomendasi">🤝 ${r.bonus} bonus</span>` : ''}${r.tglHangus ? `<br><span style="font-size:10.5px;color:var(--warn)" title="Poin yang hangus paling dekat">${r.hangusNanti} hangus ${esc(r.tglHangus)}</span>` : ''}${r.sudahHangus ? `<br><span style="font-size:10.5px;color:var(--muted)">${r.sudahHangus} sudah hangus</span>` : ''}</td><td style="white-space:nowrap">${act || '—'}</td></tr>`;
  }).join('') || `<tr><td colspan="7" class="empty">${q ? 'Tak ada pelanggan cocok dengan pencarian ini.' : 'Tidak ada pelanggan pada segmen ini.'}</td></tr>`;

  const withWa = shown.filter((r) => wa62(r.wa ?? ''));
  root.innerHTML = `<div class="ttl">🤝 CRM / Loyalty <span class="pill" style="background:var(--ice);color:var(--navy)">${shown.length}</span></div>${bar}
    <div class="scope-note">Segmentasi + Lifetime Value (LTV = Σ omzet) + poin loyalti (1 poin / ${rp(svc.poinRate())} belanja). Broadcast WA massal (n8n) menyusul saat deploy.</div>
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;margin:10px 0;flex-wrap:wrap">
      <label style="font-size:13px;color:var(--muted)">Segmen</label>
      <select id="cr_seg" aria-label="Saring segmen">${SEGS.map(([k, l]) => `<option value="${k}" ${seg === k ? 'selected' : ''}>${l}</option>`).join('')}</select>
      ${searchBoxHTML('cr_cari', crQ, '🔎 Cari nama / WA / kota…', 'Cari pelanggan')}
      ${(crQ || seg !== 'all') ? `<button class="btn" id="cr_reset" style="padding:5px 10px" title="Bersihkan pencarian & segmen" aria-label="Bersihkan pencarian dan segmen">↺</button>` : ''}
      ${withWa.length ? `<button class="btn primary" id="cr_bc">📣 Broadcast WA (${withWa.length})</button>` : ''}
      <span style="margin-left:auto;font-size:12px;color:var(--muted)">Tampil <b>${shown.length}</b>${q ? ` dari ${totalSeg}` : ''}</span></div>
    <div class="scope-note" style="margin:-4px 0 8px;font-size:11.5px">Tanda: 🔁 pelanggan sering (≥3 servis) · 💤 lama tak datang (&gt;90 hari) · 🎂 ulang tahun bulan ini</div>
    <table class="data"><thead><tr><th>Pelanggan</th><th>Kat</th><th>LTV</th><th>Trx</th><th>Terakhir</th><th>Poin</th><th>Aksi</th></tr></thead><tbody>${body}</tbody></table>`;

  pasangTab();
  const refresh = (): void => { void renderCRM(root); };
  (root.querySelector('#cr_seg') as HTMLSelectElement).addEventListener('change', (e) => { seg = (e.target as HTMLSelectElement).value; refresh(); });
  wireSearchBox(root, 'cr_cari', (v) => { crQ = v; }, refresh);
  root.querySelector('#cr_reset')?.addEventListener('click', () => { crQ = ''; seg = 'all'; refresh(); });
  root.querySelectorAll<HTMLElement>('button[data-wa]').forEach((b) => b.addEventListener('click', () => openWA(b.dataset.wa, fillTpl(waTpl().terimaKasih, { nama: b.dataset.nama ?? '' }))));
  root.querySelector('#cr_bc')?.addEventListener('click', () => void broadcastDialog(withWa));
  root.querySelectorAll<HTMLElement>('button[data-link]').forEach((b) => b.addEventListener('click', () => void kirimTautanPoin(+(b.dataset['link'] ?? 0), b.dataset['wa'] ?? '', b.dataset['nama'] ?? '')));
  root.querySelectorAll<HTMLElement>('button[data-redeem]').forEach((b) => b.addEventListener('click', () => {
    const row = shown.find((x) => x.id === +(b.dataset.redeem ?? 0));
    redeemDialog(+(b.dataset.redeem ?? 0), +(b.dataset.avail ?? 0), b.dataset.nama ?? '', refresh,
      row ? { servis: Math.max(0, row.avail - row.bonus), afiliasi: row.bonus } : undefined);
  }));
  root.querySelectorAll<HTMLElement>('button[data-poinhist]').forEach((b) => b.addEventListener('click', () => void poinHistoryDialog(+(b.dataset.poinhist ?? 0), b.dataset.nama ?? '', +(b.dataset.e ?? 0), +(b.dataset.rd ?? 0))));
}

/** Riwayat poin: earned (dari LTV) + ledger penukaran per pelanggan. Port poinHistory v0.18 (Batch 36). */
async function poinHistoryDialog(id: number, nama: string, earned: number, redeemed: number): Promise<void> {
  let reds: repo.Redemption[];
  try { reds = await repo.redemptionsForCustomer(id); } catch (e) { toast((e as Error).message); return; }
  redeemed = reds.reduce((a, r) => a + r.points, 0); // re-derive dari ledger fresh (header = jumlah ledger, tak drift)
  const avail = Math.max(0, earned - redeemed);
  // Pemberian (bonus rekomendasi) ikut ditampilkan — termasuk yang DITARIK beserta alasannya,
  // supaya pertanyaan "kenapa poin saya berkurang" bisa dijawab dengan bukti, bukan tebakan.
  let grants: afRepo.Grant[] = [];
  try { grants = await afRepo.grantsFor(id); } catch { /* afiliasi opsional */ }
  const barisGrant = grants.map((g) => `<div class="info-row"><span class="k">${esc(g.tgl)}</span><span>${
    g.ditarik_at
      ? `<span style="color:var(--danger)">↩️ −${g.poin} poin ditarik</span> · ${esc(g.ditarik_alasan ?? '')} <span style="color:var(--muted);font-size:11px">oleh ${esc(g.ditarik_by ?? '—')}</span>`
      : `<span style="color:var(--ok)">🤝 +${g.poin} poin</span> · ${esc(g.alasan)} <span style="color:var(--muted);font-size:11px">oleh ${esc(g.by_user ?? '—')}</span>`
  }</span></div>`).join('');
  const barisTukar = reds.map((r) => `<div class="info-row"><span class="k">${esc((r.created_at || '').slice(0, 10))}</span><span>🎁 −${r.points} poin${r.note ? ' · ' + esc(r.note) : ''} <span style="color:var(--muted);font-size:11px">oleh ${esc(r.by_user ?? '—')}</span></span></div>`).join('');
  const ledger = (barisGrant + barisTukar) || '<div class="empty">Belum ada penukaran atau bonus poin.</div>';
  const bg = openModal(`<div class="modal-head"><h3>🧾 Riwayat Poin — ${esc(nama)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="stat-grid" style="margin-bottom:10px">
        <div class="stat"><div class="lbl">Diperoleh</div><div class="num" style="color:var(--ok)">${earned}</div></div>
        <div class="stat"><div class="lbl">Ditukar</div><div class="num" style="color:var(--warn)">${redeemed}</div></div>
        <div class="stat"><div class="lbl">Tersedia</div><div class="num" style="color:var(--navy)">${avail}</div></div></div>
      <div class="ttl" style="font-size:14px;margin:6px 0 4px">Penukaran (${reds.length})</div>${ledger}</div>
    <div class="modal-foot"><button class="btn primary" id="ph_close">Tutup</button></div>`);
  bg.querySelector('#ph_close')?.addEventListener('click', () => bg.remove());
}

async function broadcastDialog(list: Row[]): Promise<void> {
  // Katalog hadiah ikut ditawarkan di pesan: blast yang cuma bilang "poin Anda 340" tak
  // membuat orang datang; yang membuat datang adalah tahu 340 itu bisa jadi apa.
  let hadiah: loyaltyRepo.Hadiah[] = [];
  try { hadiah = (await loyaltyRepo.listHadiah(true)).filter(berlakuBulanIni); } catch { /* katalog opsional */ }
  const daftarHadiah = hadiah.map((h) => `• ${h.nama} (${h.poin} poin)`).join('\n');

  const waBadge = (r: Row): string => r.waCount > 0
    ? `<span class="bc_badge" id="bc_bd_${r.id}" data-c="${r.waCount}" style="font-size:10.5px;color:var(--ok)">📤 ${r.waCount}× ${r.waLast ? '· ' + esc(r.waLast) : ''}</span>`
    : `<span class="bc_badge" id="bc_bd_${r.id}" data-c="0" style="font-size:10.5px;color:var(--muted)">belum di-WA</span>`;

  const bg = openModal(`<div class="modal-head"><h3>📣 Broadcast WA — ${list.length} pelanggan</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Blast massal otomatis menyusul (n8n saat deploy). Sekarang: <b>pilih</b> pelanggan lalu <b>tandai sudah dikirim</b> (atau klik 💬 per orang). Pesannya sudah terisi poin masing-masing.</div>
      <div class="field"><label>Pesan</label><textarea id="bc_msg" rows="8">${esc(waTpl().broadcast)}</textarea>
        <div class="hint">Isian otomatis: <b>{nama}</b> · <b>{poin}</b> poin tersedia · <b>{hangus}</b> tanggal poin terdekat hangus · <b>{hadiah}</b> daftar hadiah bulan ini · <b>{link}</b> tautan poin pelanggan</div></div>
      ${hadiah.length ? `<div class="scope-note">Hadiah bulan ini: ${esc(hadiah.map((h) => `${h.nama} (${h.poin})`).join(' · '))}</div>`
        : '<div class="scope-note" style="background:var(--warnbg);color:var(--warn)">Belum ada hadiah aktif bulan ini — isi katalog di Pengaturan → 🎁 Poin & Undian supaya blast ini ada tawarannya.</div>'}
      <div class="toolbar" style="display:flex;gap:8px;align-items:center;margin:8px 0;flex-wrap:wrap">
        <label style="font-size:12.5px;display:flex;gap:5px;align-items:center"><input type="checkbox" id="bc_all"> Pilih semua</label>
        <span id="bc_selinfo" style="font-size:12px;color:var(--muted)">0 terpilih</span>
        <button class="btn primary" id="bc_mark" style="padding:4px 10px" disabled>✓ Tandai terpilih sudah dikirim</button>
      </div>
      <div style="max-height:34vh;overflow:auto">${list.map((r) => `<div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid var(--line);font-size:13px">
        <label style="display:flex;gap:7px;align-items:flex-start;cursor:pointer;flex:1"><input type="checkbox" class="bc_ck" data-id="${r.id}" style="margin-top:3px">
          <span>${esc(r.nama)} · ${esc(bisaBuka('pelanggan.telepon') ? wa62(r.wa ?? '') : maskPhone(r.wa))}<br><span style="color:var(--muted);font-size:11.5px">${r.avail} poin${r.tglHangus ? ` · ${r.hangusNanti} hangus ${esc(r.tglHangus)}` : ''}</span> · ${waBadge(r)}</span></label>
        <button class="btn" style="padding:2px 8px;flex:none" data-w="${esc(wa62(r.wa ?? ''))}" data-id="${r.id}" data-n="${esc(r.nama)}" data-p="${r.avail}" data-h="${esc(r.tglHangus ?? '')}" title="Kirim broadcast WA" aria-label="Kirim broadcast WA ke ${esc(r.nama)}">💬</button></div>`).join('')}</div></div>
    <div class="modal-foot"><button class="btn" id="bc_close">Tutup</button></div>`);

  const msg = (): string => (bg.querySelector('#bc_msg') as HTMLTextAreaElement).value;
  bg.querySelector('#bc_close')?.addEventListener('click', () => bg.remove());

  // Update badge "sudah di-WA" satu baris tanpa muat ulang seluruh dialog.
  const bumpBadge = (id: number): void => {
    const el = bg.querySelector(`#bc_bd_${id}`) as HTMLElement | null; if (!el) return;
    const c = (+(el.dataset['c'] ?? '0')) + 1; el.dataset['c'] = String(c);
    el.style.color = 'var(--ok)'; el.textContent = `📤 ${c}× · ${todayISO()}`;
  };
  const checked = (): HTMLInputElement[] => [...bg.querySelectorAll<HTMLInputElement>('.bc_ck:checked')];
  const syncSel = (): void => {
    const n = checked().length;
    (bg.querySelector('#bc_selinfo') as HTMLElement).textContent = `${n} terpilih`;
    (bg.querySelector('#bc_mark') as HTMLButtonElement).disabled = n === 0;
  };
  bg.querySelectorAll<HTMLInputElement>('.bc_ck').forEach((c) => c.addEventListener('change', syncSel));
  bg.querySelector('#bc_all')?.addEventListener('change', (e) => {
    const on = (e.target as HTMLInputElement).checked;
    bg.querySelectorAll<HTMLInputElement>('.bc_ck').forEach((c) => { c.checked = on; }); syncSel();
  });
  bg.querySelector('#bc_mark')?.addEventListener('click', async () => {
    const ids = checked().map((c) => +(c.dataset['id'] ?? 0)).filter(Boolean);
    if (!ids.length) return;
    const btn = bg.querySelector('#bc_mark') as HTMLButtonElement; btn.disabled = true;
    try {
      const n = await repo.tandaiWA(ids);
      ids.forEach(bumpBadge);
      checked().forEach((c) => { c.checked = false; });
      const all = bg.querySelector('#bc_all') as HTMLInputElement | null; if (all) all.checked = false;
      syncSel(); toast(`✓ ${n} pelanggan ditandai sudah dikirim WA`);
    } catch (err) { toast((err as Error).message); btn.disabled = false; }
  });
  bg.querySelectorAll<HTMLElement>('button[data-w]').forEach((b) => b.addEventListener('click', async () => {
    // Tautan poin dibuat saat dikirim (bukan untuk semua orang sekaligus): token hanya lahir
    // untuk pelanggan yang benar-benar dihubungi.
    const id = +(b.dataset['id'] ?? 0);
    let link = '';
    try { link = `${location.origin}/poin.html#t=${await loyaltyRepo.tokenPoin(id)}`; } catch { /* tautan opsional */ }
    // fillTpl WAJIB: tanpa ini "{nama}" terkirim mentah ke tiap pelanggan.
    openWA(b.dataset['w'], fillTpl(msg(), {
      nama: b.dataset['n'] ?? '', poin: b.dataset['p'] ?? '0',
      hangus: b.dataset['h'] || 'tidak ada masa berlaku',
      hadiah: daftarHadiah || '(belum ada hadiah bulan ini)', link,
    }));
    try { await repo.tandaiWA([id]); bumpBadge(id); } catch { /* penanda gagal — pesan tetap terkirim */ }
  }));
}

/** Hadiah yang periodenya mencakup hari ini (program bulanan). */
const berlakuBulanIni = (h: loyaltyRepo.Hadiah): boolean => {
  const hari = todayISO();
  return (!h.mulai || h.mulai <= hari) && (!h.sampai || h.sampai >= hari);
};

function redeemDialog(id: number, avail: number, nama: string, done: () => void, sumber?: { servis: number; afiliasi: number }): void {
  const ambangKtp = svc.poinAmbangKtp();
  const bg = openModal(`<div class="modal-head"><h3>🎁 Tukar Poin — ${esc(nama)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Poin tersedia: <b>${avail}</b>. Minta pelanggan membuka <b>tautan poin</b> di HP-nya lalu memilih hadiah — <b>kode 6 angka</b> akan muncul di layar HP pelanggan. Masukkan kode itu di sini.</div>
      <div class="field"><label>Kode dari HP pelanggan</label>
        <input id="rd_kode" inputmode="numeric" autocomplete="off" maxlength="6" placeholder="6 angka"
          style="font-size:26px;letter-spacing:8px;text-align:center;font-weight:700">
        <div class="hint">Kode berlaku <b>10 menit</b> sejak pelanggan menekan tukar. Lewat itu, minta pelanggan menekan tukar lagi.</div>
      </div>
      <div id="rd_info" class="scope-note" style="display:none"></div>
      ${ambangKtp > 0 ? `<div class="scope-note" style="background:var(--warnbg);color:var(--warn)">Penukaran <b>≥ ${ambangKtp} poin</b> wajib memeriksa KTP pelanggan lebih dulu.</div>` : ''}
      <div class="err" id="rd_err"></div></div>
    <div class="modal-foot"><button class="btn" id="rd_cancel">Batal</button><button class="btn primary" id="rd_go">Tukar</button></div>`);

  const inp = bg.querySelector('#rd_kode') as HTMLInputElement;
  const err = bg.querySelector('#rd_err') as HTMLElement;
  const info = bg.querySelector('#rd_info') as HTMLElement;
  const tombol = bg.querySelector('#rd_go') as HTMLButtonElement;
  inp.focus();
  // Hanya angka: kasir sering mengetik cepat sambil melihat layar pelanggan.
  inp.addEventListener('input', () => { inp.value = inp.value.replace(/\D/g, '').slice(0, 6); err.textContent = ''; });
  inp.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') tombol.click(); });

  // Tampilkan sisa waktu kode yang sedang hidup — supaya kasir tahu kapan harus minta ulang,
  // bukan menebak-nebak kenapa kodenya ditolak.
  let timer = 0;
  const pantau = async (): Promise<void> => {
    try {
      const k = await loyaltyRepo.kodeAktif(id);
      if (!k) { info.style.display = 'none'; return; }
      const tick = (): void => {
        const sisa = Math.max(0, Math.floor((new Date(k.expires_at).getTime() - Date.now()) / 1000));
        if (!bg.isConnected) { window.clearInterval(timer); return; }
        info.style.display = '';
        info.innerHTML = sisa
          ? `⏳ Pelanggan sudah meminta kode — berlaku <b>${Math.floor(sisa / 60)}:${String(sisa % 60).padStart(2, '0')}</b> lagi.`
          : '⌛ Kode terakhir sudah lewat 10 menit — minta pelanggan menekan tukar lagi di HP-nya.';
      };
      tick(); timer = window.setInterval(tick, 1000);
    } catch { /* info opsional */ }
  };
  void pantau();
  const tutup = (): void => { window.clearInterval(timer); bg.remove(); };
  bg.querySelector('#rd_cancel')?.addEventListener('click', tutup);
  bg.querySelector('.x')?.addEventListener('click', () => window.clearInterval(timer));

  tombol.addEventListener('click', async () => {
    const kode = inp.value.trim();
    if (!kodeTukarValid(kode)) { err.textContent = 'Kode harus 6 angka.'; inp.focus(); return; }
    tombol.disabled = true;                       // klik ganda = dua percobaan atas kode yang sama
    try {
      // Harga hadiah baru diketahui server (kode → hadiah), jadi potongannya dilakukan server
      // lebih dulu; pembagian sumber poin dicatat sesudahnya memakai harga yang dikembalikan.
      const h = await loyaltyRepo.tukarDenganKode(id, kode, currentUser()?.nama ?? '');
      if (!h.ok) { err.textContent = h.pesan ?? 'Kode tidak berlaku.'; tombol.disabled = false; inp.select(); return; }
      if (sumber && h.poin) {
        const bagi = bagiPemakaian(h.poin, sumber.servis, sumber.afiliasi, svc.porsiServisPersen());
        if (bagi.cukup) { try { await loyaltyRepo.catatSumberPoin(id, h.poin, bagi.servis, bagi.afiliasi); } catch { /* pencatatan porsi opsional */ } }
      }
      tutup();
      toast(`🎁 ${h.hadiah} ditukar — ${h.poin} poin dipotong${h.beban ? ` · beban ${rp(h.beban)} tercatat` : ''}`);
      done();
    } catch (e) { err.textContent = (e as Error).message; tombol.disabled = false; }
  });
}

/** Kirim tautan poin ke WhatsApp pelanggan (pelanggan datang ke toko lalu membukanya di HP). */
async function kirimTautanPoin(id: number, wa: string, nama: string): Promise<void> {
  try {
    const token = await loyaltyRepo.tokenPoin(id);
    const url = `${location.origin}/poin.html#t=${token}`;
    openWA(wa, `Halo ${nama}, ini tautan poin loyalti Anda di iFiCs:\n${url}\n\nBuka untuk melihat poin & menukar hadiah. Tunjukkan kode yang muncul ke kasir ya 🙏`);
  } catch (e) { toast((e as Error).message); }
}

